/**
 * Admin → Feeds: live delivery to traders (connected traders, how late prices reach them, each ws
 * server's load), which provider prices each instrument and how (stream slot or REST), which show an
 * indicative (mock) price and why, and the state of each provider connection. Read from
 * GET /api/admin/feeds (the feeder and each ws server write their figures every few seconds) and
 * refreshed every 5 s. Keys and routing are set in Admin → Data providers. Below it, the market data
 * behind the Markets and asset pages (GET /api/admin/market-data): third-party sources, logos and
 * chart history, refreshed every minute.
 */
import { useEffect, useState } from 'react';
import {
  FEED_REASON_TEXT,
  fmt,
  fmtDateTime,
  PROVIDER_BY_ID,
  ROUTE_CLASS_LABEL,
  ROUTE_CLASSES,
  type FeedAdapterStatus,
  type FeedInstrumentStatus,
  type FeedRouting,
  type FeedSource,
  type FeedState,
  type FeedsStatus,
  type MarketDataStatus,
  type WsGatewayStats,
} from '@platform/shared';
import { InstrumentIcon } from '../components/InstrumentIcon';
import { ClassBadge, cx, Empty, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

type Data = {
  feeder: boolean;
  status: FeedsStatus | null;
  gateways: WsGatewayStats[];
  config: { routing: FeedRouting; configured: Record<string, boolean>; forceMock: boolean; allowMockTrading: boolean };
};

const SOURCE_LABEL = (s: FeedSource) => (s === 'mock' ? 'Mock feed' : PROVIDER_BY_ID[s].label);
const STATE_LABEL: Record<FeedState, string> = { live: 'Live', indicative: 'Indicative', closed: 'Closed', waiting: 'Waiting' };
const STATE_CLS: Record<FeedState, string> = { live: 'bg-up-bg text-up', indicative: 'bg-warn-bg text-warn', closed: 'bg-raised text-muted', waiting: 'bg-raised text-muted' };
const ORDER: Record<FeedState, number> = { indicative: 0, waiting: 1, live: 2, closed: 3 };

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
/** The Why column wraps instead of running under the columns after it. */
const tdWrap = 'px-3 py-2 min-w-[280px] max-w-[440px] whitespace-normal break-words';

function ago(t: number | null, now: number): string {
  if (!t) return 'never';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return fmtDateTime(t, { year: false });
}

/** An instrument that is not on a live price while it should be. */
const isProblem = (i: FeedInstrumentStatus) => i.state === 'indicative' || (i.state === 'waiting' && i.reason !== null);

function StatePill({ i }: { i: FeedInstrumentStatus }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cx('inline-block rounded-md px-2 py-0.5 text-[11px] font-semibold', STATE_CLS[i.state])}>{STATE_LABEL[i.state]}</span>
      {i.polled && (
        <span className="rounded-full border border-line px-1.5 text-[10px] text-muted" title="The stream is silent on this symbol; its price comes from the provider's REST endpoint">
          via REST
        </span>
      )}
    </span>
  );
}

function AdapterCard({ a, now }: { a: FeedAdapterStatus; now: number }) {
  const cls = a.state === 'up' ? 'text-up' : a.state === 'down' ? 'text-down' : 'text-muted';
  const label = a.state === 'up' ? 'Connected' : a.state === 'down' ? 'Down' : a.configured ? 'Not used' : 'No key';
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl border border-line bg-panel p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold">{SOURCE_LABEL(a.name)}</span>
        <span className={cx('font-semibold', cls)}>{label}</span>
      </div>
      <span className="text-muted">
        {a.instruments} instrument{a.instruments === 1 ? '' : 's'}
        {a.since && a.state !== 'off' ? ` · ${a.state === 'up' ? 'up' : 'down'} since ${ago(a.since, now)}` : ''}
        {a.detail && a.state === 'down' ? ` (${a.detail})` : ''}
      </span>
      {a.state !== 'off' && a.name !== 'mock' && (
        <span className="text-muted">
          Stream: {a.streaming}
          {a.slots !== null ? ` of ${a.slots} slots` : ' symbols'}
        </span>
      )}
      {a.restPerMin !== null && (
        <span className="text-muted">
          REST fallback: {a.restPerMin > 0 ? `${a.restPerMin} calls/min, polling ${a.polling} symbol${a.polling === 1 ? '' : 's'}` : 'off'}
        </span>
      )}
      {a.lastError && (
        <span className="text-down" title={a.lastErrorAt ? fmtDateTime(a.lastErrorAt) : undefined}>
          Provider said: “{a.lastError}”{a.lastErrorAt ? ` (${ago(a.lastErrorAt, now)})` : ''}
        </span>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: 'up' | 'warn' }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-xl border border-line bg-panel px-4 py-3">
      <span className="text-[11px] font-semibold text-muted">{label}</span>
      <span className={cx('text-xl font-bold tabular-nums', tone === 'up' && 'text-up', tone === 'warn' && 'text-warn')}>{value}</span>
      <span className="text-[11px] text-muted">{sub}</span>
    </div>
  );
}

/** Live delivery to traders: what each ws server reports, and who holds the providers' stream slots. */
function Delivery({ gateways, status }: { gateways: WsGatewayStats[]; status: FeedsStatus }) {
  const traders = gateways.reduce((n, g) => n + g.clients, 0);
  const slow = gateways.reduce((n, g) => n + g.slow, 0);
  const p50s = gateways.map((g) => g.delayP50).filter((x): x is number => x !== null);
  const p95s = gateways.map((g) => g.delayP95).filter((x): x is number => x !== null);
  const p50 = p50s.length ? Math.max(...p50s) : null;
  const p95 = p95s.length ? Math.max(...p95s) : null;
  const open = status.instruments.filter((i) => i.open);
  const liveOpen = open.filter((i) => i.state === 'live').length;
  const limited = status.adapters.filter((a) => a.state !== 'off' && a.slots !== null && a.slots > 0);
  const polled = status.instruments.filter((i) => i.via === 'poll').length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-3">
        <Kpi label="Connected traders" value={traders.toLocaleString()} sub={`across ${gateways.length} ws server${gateways.length === 1 ? '' : 's'}`} />
        <Kpi label="Price delay to browser" value={p50 === null ? '—' : `${p50} ms`} sub={p95 === null ? 'no price traffic yet' : `p50 · p95 ${p95} ms`} tone={p95 !== null && p95 > 500 ? 'warn' : 'up'} />
        <Kpi label="Open markets live" value={`${liveOpen} / ${open.length}`} sub={`${status.instruments.length - open.length} markets closed`} tone={liveOpen < open.length ? 'warn' : 'up'} />
        <Kpi label="Slow connections" value={String(slow)} sub="getting the latest price only" tone={slow ? 'warn' : undefined} />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse text-xs">
            <thead className="border-b border-line">
              <tr>
                {['WS server', 'Traders', 'Msgs/sec', 'KB/sec', 'CPU', 'Memory', 'Delay p95'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gateways.length === 0 && (
                <tr>
                  <td className={cx(td, 'text-muted')} colSpan={7}>
                    No ws server is reporting. Check <code>docker compose ps ws</code>.
                  </td>
                </tr>
              )}
              {gateways.map((g, i) => (
                <tr key={g.id} className="border-b border-line" title={g.id}>
                  <td className={td}>ws-{i + 1}</td>
                  <td className={cx(td, 'num')}>{g.clients}</td>
                  <td className={cx(td, 'num')}>{g.framesPerSec.toLocaleString()}</td>
                  <td className={cx(td, 'num')}>{Math.round(g.bytesPerSec / 1024).toLocaleString()}</td>
                  <td className={cx(td, 'num', g.cpuPct > 70 && 'text-warn')}>{g.cpuPct}%</td>
                  <td className={cx(td, 'num')}>{g.memMb} MB</td>
                  <td className={cx(td, 'num', g.delayP95 !== null && g.delayP95 > 500 && 'text-warn')}>{g.delayP95 === null ? '—' : `${g.delayP95} ms`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse text-xs">
            <thead className="border-b border-line">
              <tr>
                {['Stream slots', 'Used', 'Given to'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {limited.map((a) => {
                const mine = status.instruments.filter((i) => i.source === a.name && i.via === 'stream');
                const held = mine.filter((i) => i.positions > 0).length;
                const watched = mine.filter((i) => !i.positions && i.viewers > 0).length;
                return (
                  <tr key={a.name} className="border-b border-line">
                    <td className={td}>{SOURCE_LABEL(a.name)}</td>
                    <td className={cx(td, 'num', a.streaming >= (a.slots ?? 0) && 'text-warn')}>
                      {a.streaming} / {a.slots}
                    </td>
                    <td className={cx(td, 'text-muted')}>
                      {held} with open positions · {watched} on screen · {mine.length - held - watched} other
                    </td>
                  </tr>
                );
              })}
              <tr className="border-b border-line">
                <td className={td}>REST (no slot)</td>
                <td className={cx(td, 'num')}>{polled}</td>
                <td className={cx(td, 'text-muted')}>polled every second or two</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PriceFeeds() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'problems' | 'all'>('problems');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const d = await api.get<Data>('/api/admin/feeds');
        if (!on) return;
        setData(d);
        setError(null);
        setNow(Date.now());
      } catch (e) {
        if (on) setError(e instanceof ApiError ? e.message : 'Failed to load');
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 5000);
    return () => {
      on = false;
      window.clearInterval(t);
    };
  }, []);

  if (!data) {
    return error ? (
      <p className="m-0 text-xs text-down" role="alert">
        {error}
      </p>
    ) : (
      <div className="text-muted">
        <Spinner /> Loading feed status…
      </div>
    );
  }

  const cfg = data.config;
  const configLine = (
    <p className="m-0 text-xs text-muted">
      {ROUTE_CLASSES.map((c) => (
        <span key={c} className="mr-3 inline-block">
          {ROUTE_CLASS_LABEL[c]}: <b className="font-semibold text-fg">{cfg.routing[c].map((p) => PROVIDER_BY_ID[p].label + (cfg.configured[p] ? '' : ' (no key)')).join(' → ')}</b>
        </span>
      ))}
      {cfg.forceMock ? ' · FEEDS_FORCE_MOCK is on: every price is mock' : ''}
      {cfg.allowMockTrading ? ' · live accounts may trade on indicative prices (FEEDS_ALLOW_MOCK_TRADING)' : ' · live accounts cannot trade on indicative prices'}
      {' · keys and routing: Admin → Data providers'}
    </p>
  );

  if (!data.feeder || !data.status) {
    return (
      <div className="flex flex-col gap-3">
        {configLine}
        <Empty>
          The feeder is not running, or has not written its status yet. Check <code>docker compose logs feeder</code>.
        </Empty>
      </div>
    );
  }

  const s = data.status;
  const counts = s.instruments.reduce(
    (acc, i) => {
      acc[i.state]++;
      return acc;
    },
    { live: 0, indicative: 0, closed: 0, waiting: 0 } as Record<FeedState, number>,
  );
  const problems = s.instruments.filter(isProblem);
  const rows = [...(view === 'problems' ? problems : s.instruments)].sort((a, b) => ORDER[a.state] - ORDER[b.state] || a.symbol.localeCompare(b.symbol));
  const chip = (id: 'problems' | 'all', label: string) => (
    <button key={id} type="button" aria-pressed={view === id} onClick={() => setView(id)} className={cx('h-8 rounded-full border px-3 text-xs font-semibold', view === id ? 'border-accent bg-accent text-white' : 'border-line text-muted hover:text-fg')}>
      {label}
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      {configLine}
      <Delivery gateways={data.gateways} status={s} />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-md bg-up-bg px-2 py-0.5 font-semibold text-up">{counts.live} live</span>
        <span className={cx('rounded-md px-2 py-0.5 font-semibold', counts.indicative ? 'bg-warn-bg text-warn' : 'bg-raised text-muted')}>{counts.indicative} indicative</span>
        <span className="rounded-md bg-raised px-2 py-0.5 font-semibold text-muted">{counts.closed} closed</span>
        {counts.waiting > 0 && <span className="rounded-md bg-raised px-2 py-0.5 font-semibold text-muted">{counts.waiting} waiting</span>}
        <span className="text-muted">· status {ago(s.t, now)}</span>
        {error && (
          <span className="text-down" role="alert">
            {error}
          </span>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {s.adapters.map((a) => (
          <AdapterCard key={a.name} a={a} now={now} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {chip('problems', `Problems · ${problems.length}`)}
        {chip('all', `All · ${s.instruments.length}`)}
        <a href="#market-data" className="ml-auto text-xs font-semibold text-accent-fg no-underline">
          Market data sources (economic calendar, EIA, Finnhub …) ↓
        </a>
      </div>
      {rows.length === 0 ? (
        <Empty>{view === 'problems' ? 'Every instrument with an open market is on a live price.' : 'No instruments.'}</Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full border-collapse text-xs" style={{ minWidth: 1000 }}>
            <thead className="border-b border-line">
              <tr>
                {['Symbol', 'Class', 'Source', 'Upstream symbol', 'Price', 'Why', 'Last live quote', 'Mark'].map((h) => (
                  <th key={h} className={th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.symbol} className="border-b border-line">
                  <td className={cx(td, 'font-semibold')}>
                    <span className="flex items-center gap-2">
                      <InstrumentIcon symbol={i.symbol} size={18} />
                      <span className="flex flex-col leading-tight">
                        {i.symbol}
                        <span className="text-[10.5px] font-normal text-muted">{i.name}</span>
                      </span>
                    </span>
                  </td>
                  <td className={td}>
                    <ClassBadge cls={i.assetClass} />
                  </td>
                  <td className={cx(td, 'text-muted')}>
                    {SOURCE_LABEL(i.source)}
                    {i.via && <span className="ml-1.5 rounded-full border border-line px-1.5 text-[10px]">{i.via === 'stream' ? 'stream' : 'REST'}</span>}
                    {i.chain.length > 1 && <span className="block text-[10px]">then {i.chain.filter((c) => c !== i.source).map((c) => SOURCE_LABEL(c)).join(' → ')}</span>}
                  </td>
                  <td className={cx(td, 'num text-muted')}>{i.sourceSymbol}</td>
                  <td className={td}>
                    <StatePill i={i} />
                  </td>
                  <td className={cx(tdWrap, 'text-muted')}>
                    {i.reason ? (
                      <>
                        {FEED_REASON_TEXT[i.reason]}
                        {i.detail ? <span className="text-fg"> — {i.detail}</span> : null}
                      </>
                    ) : i.state === 'closed' ? (
                      'Market closed'
                    ) : (
                      ''
                    )}
                  </td>
                  <td className={cx(td, 'text-muted')} title={i.lastLiveAt ? fmtDateTime(i.lastLiveAt) : undefined}>
                    {ago(i.lastLiveAt, now)}
                  </td>
                  <td className={cx(td, 'num')}>{i.mark ? fmt(i.mark, i.priceDecimals) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="m-0 text-[11px] text-muted">
        Indicative = the mock feed is pricing the instrument (last live price plus a random walk). Clients see “Indicative price” on it
        {s.allowMockTrading ? '' : ' and live accounts cannot trade it until the provider delivers again'}. A provider that refuses a symbol or reports a limit is quoted under its card.
      </p>
    </div>
  );
}

/** A source's state: off (no key), working (last call succeeded), failing (last call failed) or not used yet. */
function sourceState(src: MarketDataStatus['sources'][number]): { label: string; cls: string } {
  if (!src.configured) return { label: 'Off', cls: 'text-muted' };
  const ok = src.health?.okAt ? Date.parse(src.health.okAt) : 0;
  const err = src.health?.errAt ? Date.parse(src.health.errAt) : 0;
  if (!ok && !err) return { label: 'Not used yet', cls: 'text-muted' };
  return err > ok ? { label: 'Failing', cls: 'text-down' } : { label: 'Working', cls: 'text-up' };
}

function MarketDataPanel() {
  const [data, setData] = useState<MarketDataStatus | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let on = true;
    const load = () =>
      api
        .get<MarketDataStatus>('/api/admin/market-data')
        .then((d) => {
          if (!on) return;
          setData(d);
          setFailed(null);
          setNow(Date.now());
        })
        .catch((e) => on && setFailed(e instanceof Error ? e.message : 'Could not load'));
    void load();
    const t = window.setInterval(() => void load(), 60_000);
    return () => {
      on = false;
      window.clearInterval(t);
    };
  }, []);
  const head = <h3 className="m-0 text-[13px] font-semibold">Market data (Markets and asset pages, economic calendar)</h3>;
  if (!data)
    return (
      <section id="market-data" className="flex flex-col gap-2 border-t border-line pt-4" aria-label="Market data">
        {head}
        <span className={cx('text-xs', failed ? 'text-down' : 'text-muted')}>{failed ? `Market data status could not be loaded: ${failed}` : 'Loading…'}</span>
      </section>
    );
  const at = (iso: string | null) => (iso ? ago(Date.parse(iso), now) : 'never');
  const { logos, history } = data;
  return (
    <section id="market-data" className="flex scroll-mt-4 flex-col gap-3 border-t border-line pt-4" aria-label="Market data">
      {head}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.sources.map((src) => {
          const st = sourceState(src);
          return (
            <div key={src.id} className="flex min-w-0 flex-col gap-1 rounded-xl border border-line bg-panel p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold">{src.label}</span>
                <span className={cx('font-semibold', st.cls)}>{st.label}</span>
              </div>
              <span className="text-muted">{src.shows}</span>
              {src.coverage && <span className="text-muted">Covers {src.coverage}</span>}
              {src.calls && (
                <span className="num text-muted">
                  {src.calls.today.toLocaleString()} calls today{src.calls.limit ? ` of our ${src.calls.limit.toLocaleString()} limit` : ''}
                </span>
              )}
              {src.configured && <span className="text-muted">Last success {at(src.health?.okAt ?? null)}</span>}
              {src.health?.error && st.label === 'Failing' && (
                <span className="text-down" title={src.health.errAt ? fmtDateTime(Date.parse(src.health.errAt)) : undefined}>
                  “{src.health.error}” ({at(src.health.errAt)})
                </span>
              )}
              {!src.configured && <span className="text-muted">Cards that need it are hidden from clients.</span>}
            </div>
          );
        })}
      </div>
      <p className="m-0 text-xs text-muted">
        Logos: {logos.crypto} of {logos.cryptoListed} coins, {logos.stocks} of {logos.stocksListed} stocks · last checked {at(logos.lastRunAt)} · Chart history: {history.fullYear} of {history.instruments} instruments have a full
        year of daily candles
        {history.underMonth.length > 0 && (
          <>
            {' '}
            · under a month: <span className="text-fg">{history.underMonth.slice(0, 20).join(', ')}</span>
            {history.underMonth.length > 20 ? ` and ${history.underMonth.length - 20} more` : ''}
          </>
        )}
      </p>
    </section>
  );
}

export function FeedsTab() {
  return (
    <div className="flex flex-col gap-5">
      <PriceFeeds />
      <MarketDataPanel />
    </div>
  );
}
